<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Executive Profile' : 'Create Executive Profile' }}</h1>
        <p class="subhead mb-0">Fields marked <span class="required">*</span> are required.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-card class="form-card">
      <div class="form-card-head">Profile Information</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>NAME <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_th" placeholder="เช่น นพ. สมชาย พัฒนากุล"/></v-col>
          <v-col cols="12" md="6"><label>NAME <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_en" placeholder="e.g. Dr. Somchai Pattarakul"/></v-col>
          <v-col cols="12" md="6"><label>POSITION <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.position_th" placeholder="เช่น ประธานกรรมการบริษัท"/></v-col>
          <v-col cols="12" md="6"><label>POSITION <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.position_en" placeholder="e.g. Chairman of the Board"/></v-col>
          <v-col cols="12" md="6"><label>EXECUTIVE TYPE <span class="required">*</span></label><v-select dense outlined hide-details :items="executiveTypeOptions" item-text="text" item-value="value" v-model="form.executive_type"/></v-col>
          <v-col cols="12" md="6"><label>DISPLAY ORDER <span class="required">*</span></label><v-text-field dense outlined hide-details type="number" min="0" v-model.number="form.display_order"/><div class="hint-text">Lower numbers are shown first</div></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Biography</div>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6"><label>SHORT BIO <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.biography_th" placeholder="ประวัติโดยย่อ..."/></v-col>
          <v-col cols="12" md="6"><label>SHORT BIO <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.biography_en" placeholder="Short biography..."/></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Media</div>
      <v-card-text class="pt-4">
        <input ref="profilePhotoInput" type="file" class="d-none" accept="image/*" @change="onPhotoChange" />
        <div class="media-row">
          <div class="thumb-preview">
            <img v-if="form.photo_url" :src="form.photo_url" alt="executive" @error="onImageError" />
            <div v-else class="thumb-placeholder">Photo</div>
            <button v-if="form.photo_url" type="button" class="thumb-remove" @click="removePhoto">×</button>
          </div>
          <div class="upload-box" @click="openPhotoPicker" @dragover.prevent @drop.prevent="onDropPhoto">
            <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
            Drop profile photo here or <b>Browse</b><br/><b>Upload to OBS</b>, Max file size: 3 MB
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({
  id: null,
  name_th: '',
  name_en: '',
  position_th: '',
  position_en: '',
  biography_th: '',
  biography_en: '',
  photo_url: '',
  executive_type: 'executive_management',
  display_order: 0
})

const MAX_PHOTO_SIZE_BYTES = 3 * 1024 * 1024
const EXECUTIVE_UPLOAD_FOLDER = 'corporate-content/executives'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return {
      form: defaultForm(),
      initialLoading: false,
      executiveTypeOptions: [
        { text: 'Board of Directors', value: 'board_of_directors' },
        { text: 'Executive Management', value: 'executive_management' }
      ]
    }
  },
  computed: {
    ...mapState('executives', ['list', 'isSaving']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    canSave() { return this.isEditMode ? hasPermision('CanEditExecutive') : hasPermision('CanAddExecutive') },
    isFormValid() { return !!(this.form.name_th && this.form.name_en && this.form.position_th && this.form.position_en && this.form.executive_type) }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('executives')
      await this.fetch()
      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) this.form = {...defaultForm(), ...target}
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('executives', ['setResource', 'fetch', 'save', 'upload']),
    onCancel() { this.$router.push({name: 'executive-list'}) },
    openPhotoPicker() {
      this.$refs.profilePhotoInput && this.$refs.profilePhotoInput.click()
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      return await this.upload({
        name: uploadName,
        base64,
        folder: EXECUTIVE_UPLOAD_FOLDER
      })
    },
    async assignPhoto(file) {
      if (!file || !file.type.startsWith('image/')) return
      if (file.size > MAX_PHOTO_SIZE_BYTES) return
      if (this.form.photo_url) {
        const confirmed = window.confirm('A profile photo already exists. Do you want to replace it?')
        if (!confirmed) return
      }
      const imageUrl = await this.uploadImage(file, 'executive_photo')
      if (!imageUrl) return
      this.form.photo_url = imageUrl
    },
    removePhoto() {
      this.form.photo_url = ''
    },
    onImageError(event) {
      if (event && event.target) {
        event.target.style.display = 'none'
      }
    },
    async onPhotoChange(event) {
      const file = event.target.files && event.target.files[0]
      await this.assignPhoto(file)
      event.target.value = null
    },
    async onDropPhoto(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      await this.assignPhoto(file)
    },
    async onSave() {
      const payload = {...this.form, display_order: Math.max(0, Number(this.form.display_order || 0))}
      const result = await this.$store.dispatch('executives/save', payload)
      if (!result.ok) return
      this.$router.push({name: 'executive-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.subhead { color: #64748b; font-size: 13px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e6e9f0; border-radius: 10px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 16px; font-weight: 700; color: #0f1f3b; }
.hint-text { color: #94a3b8; font-size: 11px; margin-top: 6px; }
.media-row { display: flex; gap: 16px; align-items: center; }
.thumb-preview { width: 100px; height: 100px; border-radius: 999px; overflow: visible; position: relative; border: 1px solid #e5ebf6; background: #fff; }
.thumb-preview img { width: 100%; height: 100%; object-fit: cover; border-radius: 999px; display: block; }
.thumb-placeholder { width: 100%; height: 100%;  border-radius: 999px; background: #f0f4fa; color: #98a8c8; display: flex; align-items: center; justify-content: center; font-weight: 600; }
.thumb-remove { position: absolute; top: 2px; right: 5px; width: 20px; height: 20px; border-radius: 50%; border: 0; background: rgba(198, 40, 40, .92); color: #fff; display: flex; align-items: center; justify-content: center; line-height: 1; font-size: 13px; cursor: pointer; z-index: 2; }
.upload-box { flex: 1; border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 22px; text-align: center; font-size: 13px; cursor: pointer; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.required { color: #c62828; }
.lang { color: #53596a; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
.actions-card { margin-bottom: 0; }
</style>
